import { CURR_API } from '@/constants/apis';

/**
 * The only way the browser reaches a stored file: keyon's file endpoint, by
 * the file's opaque id. keyon checks the session, MFA, who may see the file
 * and its scan state, streams the bytes itself and writes the audit row. No
 * storage host, blob name or signed URL is ever received, built or kept here,
 * and there is deliberately no fallback to a legacy path: a record without a
 * valid id renders "Document unavailable".
 */

// The same test keyon/file.php applies before it looks anything up.
export const FILE_UID_PATTERN = /^[a-f0-9]{32}$/;

// The bundle owner is a unique_ID; file.php refuses anything else with a 404.
const OWNER_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export const DOCUMENT_UNAVAILABLE_TEXT = 'Document unavailable';
// A message attachment its sender removed (keyon DeleteMessageAttachment).
export const DOCUMENT_REMOVED_TEXT = 'Document removed';
export const SCAN_PENDING_TEXT = 'Scanning for malware';
// keyon served the video and this browser would not decode it (HEVC on a desktop
// browser is the usual case): the card offers the download instead.
export const VIDEO_UNPLAYABLE_TEXT = 'This video cannot be played in this browser. Download it to watch it.';

/** True only for a 32-character lowercase hex string. */
export function isFileUid(value) {
  // `$` also matches before a trailing newline, so pin the length as well.
  return typeof value === 'string' && value.length === 32 && FILE_UID_PATTERN.test(value);
}

/** `${CURR_API}/file.php?id=<uid>` (plus `&download=1`), or null for anything that is not a uid. */
export function keyonFileUrl(uid, { download = false } = {}) {
  if (!isFileUid(uid)) return null;
  return `${CURR_API}/file.php?id=${uid}${download ? '&download=1' : ''}`;
}

/** One owner's credential documents as a zip; there is no org-wide bundle. */
export function keyonBundleUrl(ownerUniqueId) {
  if (typeof ownerUniqueId !== 'string' || !OWNER_PATTERN.test(ownerUniqueId)) return null;
  return `${CURR_API}/file.php?bundle=credentials&owner=${encodeURIComponent(ownerUniqueId)}`;
}

/**
 * What a listing or message item can offer before anyone clicks it.
 *
 * `pending` is shown as scanning for every class and every viewer: the server
 * may still serve an uploader their own image or PDF, but the page cannot tell
 * who uploaded it and shows the conservative state. An unknown or missing
 * status counts as available; file.php's 409/410 still holds and the download
 * result switches the state.
 *
 * @returns {'available'|'scan_pending'|'unavailable'}
 */
export function fileAvailability({ file_uid: fileUid, scan_status: scanStatus } = {}) {
  if (!isFileUid(fileUid)) return 'unavailable';
  if (scanStatus === 'pending') return 'scan_pending';
  if (scanStatus === 'malicious' || scanStatus === 'error') return 'unavailable';
  return 'available';
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * keyon's three file classes. `other` (office and text files) is never
 * rendered inline anywhere: download only. (A video is a fourth class on the
 * message wire; a credential listing has none.)
 * @returns {'image'|'pdf'|'other'}
 */
export function fileClassFromContentType(contentType) {
  const type = String(contentType || '').toLowerCase().split(';')[0].trim();
  if (IMAGE_TYPES.includes(type)) return 'image';
  if (type === 'application/pdf') return 'pdf';
  return 'other';
}

/**
 * PDF.js options for a file.php URL. file.php answers no Range requests, so
 * PDF.js must fetch the whole document in one plain GET.
 */
export function pdfDocumentParams(url) {
  return { url, disableRange: true, disableStream: true };
}
